import { useEffect, useRef, useState } from "react";
import ChatBell from "./ChatBell";
import ChatConversations from "./ChatConversations";
import ChatMessages from "./ChatMessages";
import ChatTyping from "./ChatTyping";

/**
 * Panel de chat de la sala.
 *
 * Solo pinta: todo el estado (mensajes, hilos, presencia, avisos) vive en
 * `useRoomChat`, para que el mismo comportamiento valga igual aquí que en
 * cualquier otra vista que quiera montar el chat de una sala.
 */
export default function RoomChat({ chat, user }) {
    const [draft, setDraft] = useState('');
    const containerRef = useRef(null);

    const {
        isModel, tab, showTab, messages, typing, conversations,
        activeThread, openThread, loadingHistory, send, signalTyping,
        notifications, status, connected, viewers, viewerCount
    } = chat;

    // Solo se desplaza el contenedor del chat: `scrollIntoView` arrastraba la
    // página entera en cada mensaje, que es el salto que se veía al chatear.
    useEffect(() => {
        const container = containerRef.current;
        if (container) container.scrollTop = container.scrollHeight;
    }, [messages, tab, activeThread]);

    // La modelo elige destinatario; el cliente siempre habla con la modelo.
    const needsThread = isModel && !activeThread;

    const submit = async (event) => {
        event.preventDefault();
        const text = draft;
        setDraft('');

        const sent = await send(text);
        if (!sent) setDraft(text);
    };

    const privateTitle = isModel
        ? conversations.find((item) => item.client_id === activeThread)?.client_name
        : null;

    return (
        <div className="chat-section">
            <div className="chat-header">
                <div className="chat-header-top">
                    <h5 className="text-white mb-0 fw-bold">
                        Chat de la Sala
                        {/* Antes una caída del socket era invisible: el chat
                            simplemente dejaba de actualizarse sin explicación. */}
                        <span className={`chat-status chat-status-${status}`} title={
                            connected ? 'Conectado en tiempo real'
                                : status === 'connecting' ? 'Reconectando...'
                                    : 'Sin conexión en tiempo real'
                        }></span>
                    </h5>

                    <ChatBell
                        notifications={notifications}
                        onOpenNotice={(notice) => {
                            if (notice.surface === 'private' && notice.thread) openThread(notice.thread);
                            else showTab('public');
                        }}
                    />
                </div>

                {!connected && (
                    <p className="chat-offline-note">
                        <i className="fa-solid fa-triangle-exclamation me-1"></i>
                        {status === 'connecting' ? 'Reconectando al chat...' : 'Chat sin conexión en vivo'}
                    </p>
                )}

                <div className="chat-tabs">
                    <button
                        className={`chat-tab-btn ${tab === 'public' ? 'active' : ''}`}
                        onClick={() => showTab('public')}
                    >
                        PÚBLICO
                        <span className="chat-tab-count" title="Personas en la sala">
                            <i className="fa-solid fa-user-group"></i> {viewerCount}
                        </span>
                    </button>
                    <button
                        className={`chat-tab-btn ${tab === 'private' ? 'active' : ''}`}
                        onClick={() => showTab('private')}
                    >
                        PRIVADO
                        {isModel && conversations.some((item) => item.unread > 0) && (
                            <span className="chat-tab-dot"></span>
                        )}
                    </button>
                </div>
            </div>

            {tab === 'private' && isModel && (
                <div className="chat-inbox">
                    {activeThread ? (
                        <button type="button" className="chat-inbox-back" onClick={() => openThread(null)}>
                            <i className="fa-solid fa-chevron-left me-2"></i>
                            {privateTitle || 'Conversación'}
                        </button>
                    ) : (
                        <span className="chat-inbox-title">
                            <i className="fa-solid fa-inbox me-2"></i>
                            Tus conversaciones ({conversations.length})
                        </span>
                    )}
                </div>
            )}

            {needsThread ? (
                <div className="chat-messages-container">
                    <ChatConversations
                        conversations={conversations}
                        activeThread={activeThread}
                        onSelect={openThread}
                    />
                </div>
            ) : (
                <ChatMessages
                    ref={containerRef}
                    messages={messages}
                    currentUserId={user?.id}
                    emptyText={loadingHistory
                        ? 'Cargando conversación...'
                        : tab === 'private'
                            ? 'Aquí solo os leéis vosotros dos. Escribe para empezar.'
                            : 'Sé el primero en saludar.'}
                />
            )}

            <div className="chat-footer">
                <ChatTyping people={typing} />

                <form onSubmit={submit} className="chat-input-wrapper">
                    <input
                        type="text"
                        className="chat-input-field"
                        placeholder={needsThread
                            ? 'Elige una conversación...'
                            : tab === 'private' ? 'Mensaje privado...' : 'Escribe un mensaje...'}
                        value={draft}
                        disabled={needsThread}
                        onChange={(event) => {
                            setDraft(event.target.value);
                            if (event.target.value) signalTyping();
                        }}
                    />
                    <button type="submit" className="chat-send-btn" disabled={needsThread || !draft.trim()}>
                        <i className="fa-solid fa-paper-plane"></i>
                    </button>
                </form>

                <div className="chat-footer-note">
                    {tab === 'private' && !isModel
                        ? <span><i className="fa-solid fa-lock me-1"></i> Solo la modelo lee esto</span>
                        : <span><i className="fa-solid fa-eye me-1"></i> {viewers.length} conectados</span>}
                </div>
            </div>
        </div>
    );
}
